import { FormikErrors, FormikTouched, useFormik } from 'formik';
import type { ObjectSchema } from 'yup';

export type UseFormReturn<T> = {
  values: T;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  handleChange: any;
  errors: FormikErrors<T>;
  touched: FormikTouched<T>;
  handleBlur: {
    (e: React.FocusEvent): void;
    <TT = any>(fieldOrEvent: TT): TT extends string ? (e: any) => void : void;
  };
  dirty: boolean;
  isValid: boolean;
  handleMaskedInputChange: (event: React.FormEvent<HTMLInputElement>, rawValue?: string) => void;
  handleSelectChange: (name: string, value: string) => void;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean,
  ) => Promise<void> | Promise<FormikErrors<T>>;
  validateField: (name: string) => Promise<void> | Promise<string>;
};

export type UseFormProps<T> = {
  initialValues: T;
  onSubmit: (values: T) => void;
  validationSchema: ObjectSchema<any>;
};

export type UseForm<T> = (props: UseFormProps<T>) => UseFormReturn<T>;

export function useForm<T>({
  initialValues,
  onSubmit,
  validationSchema,
}: UseFormProps<T>): UseFormReturn<T> {
  const {
    values,
    handleSubmit,
    handleChange,
    errors,
    touched,
    handleBlur,
    isValid,
    dirty,
    setFieldValue,
    validateField,
  } = useFormik({
    initialValues,
    onSubmit,
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
  });

  const handleMaskedInputChange = (event: React.FormEvent<HTMLInputElement>, rawValue?: string) => {
    const { name } = event.target as HTMLInputElement;
    const value = rawValue;

    setFieldValue(name, value);
  };

  const handleSelectChange = (name: string, value: string) => {
    setFieldValue(name, value, true);
  };

  return {
    handleMaskedInputChange,
    handleSelectChange,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    validateField,
    errors,
    values,
    touched,
    isValid,
    dirty,
  };
}
